import { ChangeDetectionStrategy, Component } from '@angular/core';
import { GrooveSurfaceDirective } from '@groove/angular';
import {
  ImageGalleryComponent,
  type GalleryImage,
} from '../../../../content/image-gallery/image-gallery.component';
import { LightboxImageComponent } from '../../../../content/lightbox-image/lightbox-image.component';

@Component({
  selector: 'app-gear-content',
  imports: [
    GrooveSurfaceDirective,
    ImageGalleryComponent,
    LightboxImageComponent,
  ],
  templateUrl: './gear-content.component.html',
  styleUrl: './gear-content.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GearContentComponent {
  protected readonly galleryImages: GalleryImage[] = [
    {
      src: '/images/gear/rig-art932-01.webp',
      alt: "Russ's 2008 Ken Smith with an RCF ART 932-A on a patterned carpet",
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/rig-art932x2-01.webp',
      alt: 'The rig with two RCF ART 932-A speakers',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/rig-art932x2-02.webp',
      alt: 'Russ set up with two RCF speakers at a car dealership gig',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/rig-art932-02.webp',
      alt: "Russ's bass and an RCF speaker at a waterfront gig",
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/speaker-art932-gig.webp',
      alt: 'RCF ART 932-A set up outdoors',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/speaker-art910-02.webp',
      alt: 'RCF ART 910-A powered speaker at a gig',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/bass-ks2008-gig.webp',
      alt: "Russ's 2008 Ken Smith Black Tiger at a holiday gig",
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/bass-ks2021-gig.webp',
      alt: "Russ's 2021 Ken Smith Black Tiger",
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/cart-gruv-gear.webp',
      alt: "Russ's loaded Gruv Gear cart in the snow",
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/rig-speakers-gig.webp',
      alt: "Russ's RCF powered speakers set up at a gig",
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/rig-stage-setup.webp',
      alt: "Russ's full stage rig — bass on a stand, pedalboard, and cases",
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/rig-board-metro.webp',
      alt: "Russ's Pedaltrain Metro board",
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    // 2026-07/08 email deliveries — first Florida-era rig shots (Cafe 27).
    {
      src: '/images/gear/rig-boards-gig.webp',
      alt: 'Both pedalboards deployed at a gig — the Metro board on its stand with the Trickfish chain, the effects board on the floor below',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/rig-ornate-venue.webp',
      alt: "Russ's six-string Ken Smith on a stand beside two RCF powered speakers in an ornate venue",
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/rig-full-lineup.webp',
      alt: 'The full rig lined up for load-in — gig bag, backpack, powered speaker with the Metro board on top, and the six-string Ken Smith',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/rig-cafe27.webp',
      alt: 'Stage corner at Cafe 27 in Florida — drums, both pedalboards, bass on a stand, iPad, and monitor speakers',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    // 2026-09-14 delivery (docs/source-images/gear-2026-09/).
    {
      src: '/images/gear/rig-art932-cork-wall.webp',
      alt: "Russ's six-string Ken Smith beside an RCF ART 932-A in its cover, with the Trickfish pedals set on top, against a cork wall",
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/rig-art932x2-drums.webp',
      alt: "Russ's Ken Smith on a stand in front of two RCF ART 932-A speakers, drum kit behind",
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/rig-stage-boards.webp',
      alt: 'Full stage setup at a gig: the Metro board on its stand, the Nano effects board and the AirTurn page turner on the floor, drums behind',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/gear/board-effects-floor.webp',
      alt: 'The Pedaltrain Nano effects board on a stone floor at a gig, with the AirTurn page turner beside it',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
  ];
}
